import { BellOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, InputNumber, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  COMPLIANCE_DOCUMENT_ORDER
} from "../../../SubcontractorsPage/constants/compliance.ts"
import {
  type SubcontractorAlertSettings,
  type SubcontractorAlertSettingsPayload,
  getSubcontractorAlertSettings,
  updateSubcontractorAlertSettings
} from "../../api/subcontractor-alert-settings.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

// The company's default applies until a document type is given its own; the
// server treats an empty value the same way, so this only mirrors it for the
// placeholder.
const DEFAULT_LEAD_DAYS = 14
const MIN_DAYS = 1
const MAX_DAYS = 365

// Every kind of subcontractor document that carries an expiration date, in the
// order they read on the profile page. The W-9 has no date field of its own: it
// expires a year after its validity date.
const DOCUMENT_TYPES = COMPLIANCE_DOCUMENT_ORDER

type DocumentType = string

export interface SubcontractorAlertsFormData {
  default_days?: number | null
  by_type?: Partial<Record<DocumentType, number | null>>
}

/** The values the API takes: an empty document type is left out so it falls
 * back to the default, and an empty default is sent as null (unset). */
export const toAlertSettingsPayload = (
  values: SubcontractorAlertsFormData
): SubcontractorAlertSettingsPayload => {
  const overrides: Record<string, number> = {}

  DOCUMENT_TYPES.forEach((type) => {
    const days = values.by_type?.[type]
    if (typeof days === "number") overrides[type] = days
  })

  return {
    subcontractor_compliance_alert_lead_days:
      typeof values.default_days === "number" ? values.default_days : null,
    subcontractor_compliance_alert_lead_days_by_type: overrides
  }
}

export function SubcontractorAlertsCard() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<SubcontractorAlertSettings | null>(
    null
  )
  const [saving, setSaving] = useState(false)
  // The form stays locked until the stored settings have arrived: saving an
  // untouched blank form over a failed load would wipe every override.
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)

  const [form] = Form.useForm<SubcontractorAlertsFormData>()
  const [dirty, setDirty] = useState(false)
  const defaultDays = Form.useWatch("default_days", form)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")

    void getSubcontractorAlertSettings().then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setSettings(response.data)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  const initialValues = useMemo<SubcontractorAlertsFormData>(
    () => ({
      default_days: settings?.subcontractor_compliance_alert_lead_days ?? null,
      by_type: settings?.subcontractor_compliance_alert_lead_days_by_type ?? {}
    }),
    [settings]
  )

  // Only resync from the server while there is no unsaved edit, so a slow
  // fetch resolving mid-typing never discards what was typed.
  useEffect(() => {
    if (!dirty) form.setFieldsValue(initialValues)
  }, [form, initialValues, dirty])

  const handleFinish = async (values: SubcontractorAlertsFormData) => {
    setSaving(true)
    try {
      const response = await updateSubcontractorAlertSettings(
        toAlertSettingsPayload(values)
      )
      // A rejection is already surfaced by the shared response handler.
      if (response?.ok && response.data) {
        setSettings(response.data)
        setDirty(false)
        showResponseMessage(
          response,
          t("settingsConfiguration.subcontractorAlerts.saved")
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const builtInDefault = settings?.built_in_default_days ?? DEFAULT_LEAD_DAYS
  const inheritedDefault = defaultDays ?? builtInDefault

  const daysInput = (placeholder: string) => (
    <InputNumber
      min={MIN_DAYS}
      max={MAX_DAYS}
      precision={0}
      className="w-full"
      suffix={t("settingsConfiguration.subcontractorAlerts.days")}
      placeholder={placeholder}
    />
  )

  return (
    <SettingsCard
      iconWrapperClassName="bg-amber-100"
      icon={<BellOutlined className="text-amber-600" />}
      title={t("settingsConfiguration.subcontractorAlerts.title")}
      subtitle={t("settingsConfiguration.subcontractorAlerts.subtitle")}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.subcontractorAlerts.loadError")}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t("settingsConfiguration.subcontractorAlerts.retry")}
            </Button>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        className="with-default-error"
        initialValues={initialValues}
        disabled={saving || loadState !== "loaded"}
        onValuesChange={() => setDirty(true)}
        onFinish={handleFinish}
      >
        <Form.Item
          name="default_days"
          label={t("settingsConfiguration.subcontractorAlerts.defaultLabel")}
          extra={t("settingsConfiguration.subcontractorAlerts.defaultHint")}
        >
          {daysInput(String(builtInDefault))}
        </Form.Item>

        <Typography.Text className="font-semibold text-[15px]">
          {t("settingsConfiguration.subcontractorAlerts.documentsLabel")}
        </Typography.Text>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 pt-2">
          {DOCUMENT_TYPES.map((type) => (
            <Form.Item
              key={type}
              name={["by_type", type]}
              label={COMPLIANCE_DOCUMENT_LABELS[type]}
              extra={
                type === "w9"
                  ? t("settingsConfiguration.subcontractorAlerts.w9Hint")
                  : type === "miscellaneous"
                    ? t("settingsConfiguration.subcontractorAlerts.miscHint")
                    : undefined
              }
            >
              {daysInput(String(inheritedDefault))}
            </Form.Item>
          ))}
        </div>

        <Flex justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!dirty || loadState !== "loaded"}
          >
            {t("settingsConfiguration.subcontractorAlerts.save")}
          </Button>
        </Flex>
      </Form>
    </SettingsCard>
  )
}
